import styles from './index.module.css'

import cn from 'clsx'
import get from 'lodash/get'
import React, { useEffect, useMemo, useState } from 'react'
import type { Location } from 'react-router-dom'

import { isLoggingInFromAccountsSubdomain } from '@disco/api/utils/auth'

import type { BusinessData } from 'types/business'
import type { AddToDiscoUser, LoginDetails } from 'types/user'

import { Flex, Heading, Spinner, Text } from 'components/ds'

import ForgotPasswordForm from './forgot-password-form'
import { usePromiseAsString } from './hooks'
import Instruction from './instruction'
import PlainLoginForm from './plain-login-form'
import SamlDisabled from './saml-disabled'
import type { SamlDiscoveryPayload } from './saml-discovery-form'
import SamlDiscoveryForm from './saml-discovery-form'
import SamlForm from './saml-form'
import SignUpForm from './sign-up-form'
import type {
  InitialIntent,
  LoginFormState,
  LoginFormType,
  SignUpDetails,
} from './types'
import Warning from './warning'
import { useTranslation } from '@disco/i18n/use-translation'
import { Trans } from '@disco/i18n/trans'

export type Props = {
  // The current user
  user: AddToDiscoUser
  // Initial Email for the Sign Up form
  initialEmail?: string
  // The current location
  location: Location
  // The heading/title of the login form
  heading?: string
  // A message to display to the user
  message?: string
  // Initial Intent to show when launching
  initialIntent?: InitialIntent
  // The login function to call when the user submits the form
  onLoginUser: (
    loginDetails: LoginDetails,
    returnPath: string | null | undefined,
  ) => unknown
  // maybe remove returnPath and handle externally
  // Forgot password function to call
  onForgotPassword: (
    email: string,
    returnUrl: string,
  ) => Promise<{
    success: boolean
  }>
  // Sign up user function to call
  onSignupCatalogUser?: (details: SignUpDetails) => Promise<{
    status: string
  }>
  // Supplying this will enable SAML discovery login
  onSamlDiscovery?: (email: string) => Promise<SamlDiscoveryPayload>
  // The signup URL to use, create catalog account
  onGetSignupUrl?: () => Promise<string>
  // Supplying this will let the login form detect the business login mechanism.
  onGetBusiness?: () => Promise<Pick<BusinessData, 'login_mechanism'>>
  // Optional function to call when the heading is clicked
  onHeadingClick?: () => void
  // Optional class name to pass through
  className?: string
}

/**
 * Comprehensive login form component supporting multiple authentication methods
 * Handles plain login, SAML authentication, signup, and password recovery
 * @param user - Current user object
 * @param initialEmail - Pre-filled email for signup form
 * @param location - Current location object with query parameters
 * @param heading - Form title/heading text
 * @param message - Optional message to display to user
 * @param initialIntent - Initial form state ('sign_in', 'sign_up', 'forgot_password', 'saml')
 * @param onLoginUser - Login submission handler
 * @param onForgotPassword - Password reset handler
 * @param onSignupCatalogUser - Signup handler for catalog users
 * @param onSamlDiscovery - SAML discovery handler
 * @param onGetSignupUrl - Signup URL provider
 * @param onGetBusiness - Business data provider
 * @param onHeadingClick - Heading click handler
 * @param className - Additional CSS classes
 */
const LoginForm = ({
  user,
  initialEmail,
  location,
  heading,
  message,
  initialIntent = 'sign_in',
  onLoginUser,
  onForgotPassword,
  onSignupCatalogUser,
  onSamlDiscovery,
  onGetSignupUrl,
  onGetBusiness,
  onHeadingClick,
  className,
}: Props): React.ReactNode => {
  const { t } = useTranslation('accounts')
  // Resolved here rather than as a default parameter so the fallback re-runs on
  // language change. `??` keeps the default-parameter semantics: an explicit ''
  // still suppresses the heading via the `headingText &&` guards below.
  const headingText = heading ?? t('accounts:signInToDisco', 'Sign in to DISCO')
  // The state of the login form, which can be 'loading', 'ready' etc.
  const [state, setState] = useState<LoginFormState>('init')
  // The type of login form to show, 'plain' or 'saml' are the only options
  const [type, setType] = useState<LoginFormType>('plain')
  // The number of login attempts
  const [loginAttempts, setLoginAttempts] = useState(0)
  // If SAML is enabled
  const [samlEnabled, setSamlEnabled] = useState(false)
  // Show the SAML disabled form
  const [showSamlDisabledForm, setShowSamlDisabledForm] = useState(false)
  // Get the signup and forgot password URLs if promises are provided
  const signupUrl = usePromiseAsString(onGetSignupUrl, () => setState('error'))

  const searchParams = useMemo(
    () => new URLSearchParams(location.search),
    [location.search],
  )

  // Pass through the login details to the provided login function
  const handleLogin = (loginDetails: LoginDetails) => {
    onLoginUser(
      loginDetails,
      decodeURIComponent(
        searchParams.get('return') || searchParams.get('next') || '',
      ),
    )
    setLoginAttempts(v => v + 1)
  }

  const handleForgotPassword = (email: string) => {
    const next = searchParams.get('return') || searchParams.get('next') || ''
    return onForgotPassword(email, next)
  }

  const goToSaml = () => {
    if (onGetBusiness) {
      setType('saml')
    } else {
      setType('saml_discovery')
    }
  }

  // Get the business login mechanism if getBusiness is provided
  useEffect(() => {
    if (state !== 'init') return

    const initBusiness = async () => {
      try {
        let newState: LoginFormState = 'ready'
        let newType: LoginFormType = 'plain'

        // Get the login mechanism if getBusiness is provided
        if (onGetBusiness && !isLoggingInFromAccountsSubdomain()) {
          setState('loading')
          const business = await onGetBusiness()
          newType = business.login_mechanism || 'plain'
          setSamlEnabled(newType === 'saml')
        }

        // Try and set the state to the initial intent
        if (initialIntent === 'sign_up' && onSignupCatalogUser) {
          newState = 'sign_up'
        } else if (initialIntent === 'forgot_password') {
          newState = 'forgot_password'
        } else if (initialIntent === 'saml') {
          if (onSamlDiscovery) {
            newType = 'saml_discovery'
          }
        }

        // Set the type and state to the initial intent
        setType(newType)
        setState(newState)
      } catch {
        setState('error')
      }
    }

    initBusiness()
  }, [
    onGetBusiness,
    initialIntent,
    state,
    onSignupCatalogUser,
    onForgotPassword,
    onSamlDiscovery,
  ])
  // Autologin lives on the login page, not here
  useEffect(() => {
    if (state !== 'init') return

    if (searchParams.get('reason') === 'user_not_in_business') {
      setState('user_not_in_business')
    }
  }, [searchParams.get('reason'), state])
  // Grab the user's error code
  const errorCode: string | null = get(user.error, 'code', null)
  const isTransitioning = user.loggedIn && !user.preloaded
  return (
    <Flex
      width="100%"
      direction="column"
      align="center"
      className={cn(className, styles.Wrapper)}
      data-dont-close-formats-dropdown
    >
      <Flex
        direction="column"
        minWidth="230px"
        maxWidth="400px"
        width="100%"
        gap="4"
      >
        {state === 'error' ? (
          <Warning>
            <Trans
              t={t}
              i18nKey="accounts:discoUnavailableRefreshOrContactSupport"
            >
              Sorry! DISCO is unavailable!
              <br />
              Please refresh page or{' '}
              <a href="mailto:support@disco.ac">contact support</a>.
            </Trans>
          </Warning>
        ) : state === 'init' || state === 'loading' ? (
          <Flex
            align="center"
            justify="center"
            gap="2"
            className={styles.DelayedAppear}
          >
            <Spinner />
            <Text align="center" color="gray">
              {t('accounts:loading', 'Loading...')}
            </Text>
          </Flex>
        ) : state === 'user_not_in_business' ? (
          <Instruction
            text={t(
              'accounts:pleaseCheckYourEmailForAccessLink',
              'Please check your email for access link.',
            )}
          />
        ) : state === 'forgot_password' ? (
          <ForgotPasswordForm
            onSubmit={handleForgotPassword}
            onDismiss={() => setState('ready')}
          />
        ) : state === 'sign_up' && onSignupCatalogUser ? (
          <SignUpForm
            initialEmail={initialEmail}
            onSignupUser={onSignupCatalogUser}
            onBackToLogin={() => setState('ready')}
          />
        ) : showSamlDisabledForm ? (
          <SamlDisabled onDismiss={() => setShowSamlDisabledForm(false)} />
        ) : type === 'plain' ? (
          <>
            {user.mfaCodeRequired ? (
              <Flex direction="column" align="center" mb="2">
                <Heading onClick={onHeadingClick} mt="0" mb="1">
                  {t(
                    'accounts:multifactorAuthentication',
                    'Multi-factor authentication',
                  )}
                </Heading>
                <Text size="2" color="gray" align="center">
                  {user.mfaType === 'email' ? (
                    <span>
                      {t(
                        'accounts:enterTheCodeFromYourEmail',
                        'Enter the code from your email',
                      )}
                    </span>
                  ) : (
                    <span>
                      {t(
                        'accounts:enterTheCodeFromYourAuthenticatorApp',
                        'Enter the code from your authenticator app',
                      )}
                    </span>
                  )}
                </Text>
              </Flex>
            ) : (
              headingText && (
                <Heading onClick={onHeadingClick} mt="0" mb="0">
                  {headingText}
                </Heading>
              )
            )}
            {message && (
              <Text size="2" color="gray">
                {message}
              </Text>
            )}
            <PlainLoginForm
              user={user}
              location={location}
              onSubmit={handleLogin}
              loginAttempts={loginAttempts} // Enable SAML either if in anonymous mode (no getBusiness passed in) or if SAML is enabled on the business
              samlEnabled={!!onSamlDiscovery || samlEnabled} // On SAML clicked, if anonymous business go to SAML discovery, otherwise go to main SAML
              onSamlClick={goToSaml}
              onForgotPasswordClick={() => setState('forgot_password')}
              signupUrl={signupUrl}
              signupCatalog={!!onSignupCatalogUser}
              onSignUpClick={() => setState('sign_up')}
              errorCode={errorCode}
              isTransitioning={isTransitioning}
            />
          </>
        ) : type === 'saml' ? (
          <>
            {headingText && (
              <Heading onClick={onHeadingClick} mt="0" mb="0">
                {headingText}
              </Heading>
            )}
            <SamlForm location={location} onDismiss={() => setType('plain')} />
          </>
        ) : (
          onSamlDiscovery && (
            <>
              <Heading onClick={onHeadingClick} mt="0" mb="0">
                {t('accounts:signInWithSso', 'Sign in with SSO')}
              </Heading>
              <Text size="2" color="gray">
                {t(
                  'accounts:signInUsingYourOrganizationsCredentials',
                  'Sign in using your organization’s credentials',
                )}
              </Text>
              <SamlDiscoveryForm
                onSubmit={onSamlDiscovery}
                onDismiss={() => setType('plain')}
              />
            </>
          )
        )}
      </Flex>
    </Flex>
  )
}

export default LoginForm
